// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import type {
  SingleTickerTag,
  TickersTags,
  TickerTag,
  TickerTagFeature,
} from '@shared/data-access/interfaces/api/engine/citec';

export const tickerTagsActualization = (
  feature: TickerTagFeature,
  initialGroups: Tag[],
  updatedGroups: Tag[],
  initialTickers: TickersTags[],
  updatedTickers: TickersTags[],
): TickerTag[] => {
  const groupNames = new Set(initialGroups.map(g => g.name));
  const newGroups = updatedGroups.filter(g => !groupNames.has(g.name));

  const changedTickersMap = new Map<string, string[]>();
  updatedTickers.forEach(updatedTicker => {
    const initialTicker = initialTickers.find(
      t => t.ticker === updatedTicker.ticker,
    );

    const initialTags = new Set(initialTicker!.tags);
    const newTags = updatedTicker.tags.filter(tag => !initialTags.has(tag));

    newTags.forEach(tag => {
      if (!changedTickersMap.has(tag)) {
        changedTickersMap.set(tag, []);
      }
      changedTickersMap.get(tag)?.push(updatedTicker.ticker);
    });
  });

  const result: TickerTag[] = [];
  /* istanbul ignore next */
  newGroups.forEach(group => {
    const tickersAssigned = changedTickersMap.get(group.name) || [];
    result.push({
      tickers: tickersAssigned,
      tag: group.name,
      feature: feature,
    });
  });
  /* istanbul ignore next */
  changedTickersMap.forEach((tickers, tag) => {
    if (!groupNames.has(tag)) {
      return;
    }
    result.push({
      tickers,
      tag,
      feature: feature,
    });
  });

  return result;
};

export const concatenateStrings = (array: string[]): string | null => {
  return array.length === 0 ? null : array.join(',');
};

export const groupTickersTag = (array: SingleTickerTag[]): SingleTickerTag[] => {
  return Object.entries(
    array.reduce((acc, current) => {
      const { tag, ticker } = current;
      if (!acc[tag]) {
        acc[tag] = [];
      }
      acc[tag].push(ticker);
      return acc;
    }, {} as Record<string, string[]>),
  ).map(([tag, tickers]) => ({
    tag,
    ticker: tickers.join(','),
  }));
};
